/**
 * UploadPage.tsx — страница загрузки нового видео (neobrutalism).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadForm } from '../components/UploadForm';
import { Button } from '../components/ui/button';
import { ChevronLeft, Upload } from 'lucide-react';
import { cn } from '../lib/utils';

interface ProfileInfo {
  name: string;
  label: string;
  bw: string;
  desc: string;
  activeBg: string;
}

const ALL_PROFILES: ProfileInfo[] = [
  { name: '360p',  label: '360p',  bw: '800 kbps', desc: 'Мобильный',  activeBg: 'bg-blue-200 dark:bg-blue-300' },
  { name: '480p',  label: '480p',  bw: '1.4 Mbps', desc: 'SD',         activeBg: 'bg-cyan-200 dark:bg-cyan-300' },
  { name: '720p',  label: '720p',  bw: '2.8 Mbps', desc: 'HD',         activeBg: 'bg-green-200 dark:bg-green-300' },
  { name: '1080p', label: '1080p', bw: '5 Mbps',   desc: 'Full HD',    activeBg: 'bg-yellow-200 dark:bg-yellow-300' },
  { name: '2K',    label: '2K',    bw: '8 Mbps',   desc: '1440p',      activeBg: 'bg-orange-200 dark:bg-orange-300' },
  { name: '4K',    label: '4K',    bw: '16 Mbps',  desc: '2160p',      activeBg: 'bg-red-200 dark:bg-red-300' },
];

const DEFAULT_SELECTED = ['360p', '480p', '720p'];

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>(DEFAULT_SELECTED);

  const toggleProfile = (name: string) => {
    setSelectedProfiles((prev) =>
      prev.includes(name)
        ? prev.length > 1 ? prev.filter((p) => p !== name) : prev
        : [...prev, name]
    );
  };

  const selectAll = () => setSelectedProfiles(ALL_PROFILES.map((p) => p.name));
  const selectDefault = () => setSelectedProfiles(DEFAULT_SELECTED);

  return (
    <div className="min-h-screen bg-background">
      {/* Навбар */}
      <header className="sticky top-0 z-20 border-b-2 border-foreground bg-primary text-primary-foreground">
        <div className="mx-auto flex min-h-14 max-w-6xl items-center gap-3 px-3 py-2 sm:gap-4 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="font-bold gap-1">
            <ChevronLeft className="w-4 h-4" />
            Назад
          </Button>
          <div className="hidden h-5 w-0.5 bg-primary-foreground sm:block" />
          <div className="min-w-0">
            <h1 className="text-primary-foreground font-black text-base">Загрузить видео</h1>
            <p className="text-primary-foreground/70 text-xs font-mono">ABR Streaming</p>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b-2 border-foreground stripe-bg">
        <div className="mx-auto max-w-6xl px-3 py-8 text-center sm:px-6 sm:py-12">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center border-2 border-foreground bg-primary shadow-neo sm:h-16 sm:w-16">
            <Upload className="w-7 h-7 text-foreground" />
          </div>
          <h2 className="font-black text-3xl text-foreground mb-2 tracking-tight">Загрузить видео</h2>
          <p className="text-foreground/60 text-sm max-w-md mx-auto font-medium">
            Выберите профили качества и загрузите видеофайл для HLS-транскодирования
          </p>
        </div>
      </section>

      {/* Контент */}
      <main className="mx-auto max-w-6xl px-3 py-6 sm:px-6 sm:py-10">
        <div className="max-w-xl mx-auto space-y-8">
          {/* Выбор профилей */}
          <div className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-foreground text-xs uppercase tracking-widest font-black">
                Профили качества
                <span className="ml-2 bg-primary border border-foreground px-1.5 py-0.5 font-mono">
                  {selectedProfiles.length} выбрано
                </span>
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={selectDefault}
                  className="text-xs font-bold text-foreground border-2 border-foreground px-2.5 py-1 bg-card hover:bg-muted transition-colors shadow-neo-sm hover:shadow-neo active:shadow-none active:translate-x-0.5 active:translate-y-0.5"
                >
                  SD+HD
                </button>
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs font-bold text-foreground border-2 border-foreground px-2.5 py-1 bg-card hover:bg-muted transition-colors shadow-neo-sm hover:shadow-neo active:shadow-none active:translate-x-0.5 active:translate-y-0.5"
                >
                  Все
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {ALL_PROFILES.map((profile) => {
                const active = selectedProfiles.includes(profile.name);
                return (
                  <button
                    key={profile.name}
                    type="button"
                    onClick={() => toggleProfile(profile.name)}
                    className={cn(
                      'relative border-2 border-foreground p-3 text-left transition-all duration-150',
                      active
                        ? cn(profile.activeBg, 'shadow-neo-sm -translate-x-px -translate-y-px')
                        : 'bg-card opacity-50 hover:opacity-80 hover:shadow-neo-sm'
                    )}
                  >
                    {active && (
                      <span className="absolute top-1.5 right-1.5 text-xs font-black">✓</span>
                    )}
                    <div className={cn('font-black font-mono text-sm', active ? 'text-slate-950' : 'text-foreground')}>
                      {profile.label}
                    </div>
                    <div className={cn('text-xs mt-0.5 font-mono', active ? 'text-slate-800' : 'text-foreground/60')}>
                      {profile.bw}
                    </div>
                    <div className={cn('text-xs font-medium', active ? 'text-slate-700' : 'text-foreground/50')}>
                      {profile.desc}
                    </div>
                  </button>
                );
              })}
            </div>

            {(selectedProfiles.includes('4K') || selectedProfiles.includes('2K')) && (
              <p className="text-foreground text-xs font-black border-2 border-foreground bg-orange-100 px-3 py-2 dark:bg-orange-300 dark:text-slate-950">
                ! 4K и 2K транскодирование занимает значительно больше времени
              </p>
            )}
          </div>

          <UploadForm selectedProfiles={selectedProfiles} />
        </div>
      </main>
    </div>
  );
};
